import * as React from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { CalendarCheck, GitCompareArrows } from 'lucide-react';
import { useLocale, useT } from '../lib/i18n.js';
import { listViewingBookings } from '../lib/viewings-api.js';
import { listNewMatches } from '../lib/matches-api.js';
import { formatViewingTime } from '../lib/viewing-format.js';
import { V4Group, V4Pill, V4Row } from './ux-v4.js';

/**
 * Dnešek — dva bloky kroku CRM4 (bod 3 zadání):
 *  - „Dnešní prohlídky" = rezervace přihlášeného makléře na dnešek
 *    (stejný zdroj jako karta nemovitosti a `/prohlidky`),
 *  - „Nové shody poptávek" = nejvýš 3 nejnovější + odkaz na všechny
 *    (`/parovani-poptavek`); počet je ze serveru (`total`).
 * Prázdný blok se neukazuje — Dnešek je „co teď", ne katalog funkcí.
 */

export const NEW_MATCHES_ON_DNESEK = 3;

export function TodayViewings({
  userId,
  from,
  to,
}: {
  readonly userId: string;
  readonly from: string;
  readonly to: string;
}): React.JSX.Element | null {
  const t = useT();
  const locale = useLocale();
  const bookings = useQuery({
    queryKey: ['viewing-bookings', 'today', userId, from],
    queryFn: () => listViewingBookings({ ownerUserId: userId, from, to, status: 'booked' }),
  });
  const items = bookings.data ?? [];
  if (items.length === 0) return null;
  return (
    <>
      <h2 className="rh-v4-h2" id="dnesek-viewings-h">
        {t('webAdmin.dnesek.viewingsTitle')}
      </h2>
      <V4Group testId="dnesek-viewings" label={t('webAdmin.dnesek.viewingsTitle')}>
        {items.map((b) => (
          <V4Row
            key={b.id}
            lead={formatViewingTime(b.startsAt, locale)}
            icon={
              <span className="rh-v4-ico" aria-hidden="true">
                <CalendarCheck size={16} strokeWidth={2} />
              </span>
            }
            title={b.propertyLabel ?? t('webAdmin.matches.unnamedProperty')}
            sub={b.contactName}
            chevron
            render={(className, content) => (
              <Link
                to="/nemovitosti/$id"
                params={{ id: b.propertyId }}
                className={className}
                data-testid={`dnesek-viewing-${b.id}`}
              >
                {content}
              </Link>
            )}
          />
        ))}
      </V4Group>
      <p className="rh-a2-card__foot">
        <Link to="/prohlidky" className="rh-a2-link" data-testid="dnesek-all-viewings-link">
          {t('webAdmin.dnesek.allViewingsLink')}
        </Link>
      </p>
    </>
  );
}

export function NewDemandMatches(): React.JSX.Element | null {
  const t = useT();
  const matches = useQuery({
    queryKey: ['new-matches', NEW_MATCHES_ON_DNESEK],
    queryFn: () => listNewMatches(NEW_MATCHES_ON_DNESEK),
  });
  const data = matches.data;
  if (data === undefined || data.total === 0) return null;
  return (
    <>
      <h2 className="rh-v4-h2" id="dnesek-matches-h">
        {t('webAdmin.dnesek.matchesTitle')}
      </h2>
      <V4Group testId="dnesek-matches" label={t('webAdmin.dnesek.matchesTitle')}>
        {data.items.slice(0, NEW_MATCHES_ON_DNESEK).map((m) => (
          <V4Row
            key={`${m.demandId}:${m.propertyId}`}
            icon={
              <span className="rh-v4-ico" aria-hidden="true">
                <GitCompareArrows size={16} strokeWidth={2} />
              </span>
            }
            title={`${m.demandTitle} → ${m.propertyLabel ?? t('webAdmin.matches.unnamedProperty')}`}
            sub={m.explanation}
            right={<V4Pill tone={m.score >= 80 ? 'accent' : 'neutral'}>{t('webAdmin.matches.score', { score: m.score })}</V4Pill>}
            chevron
            render={(className, content) => (
              <Link
                to="/nemovitosti/$id"
                params={{ id: m.propertyId }}
                className={className}
                data-testid={`dnesek-match-${m.demandId}-${m.propertyId}`}
              >
                {content}
              </Link>
            )}
          />
        ))}
      </V4Group>
      <p className="rh-a2-card__foot">
        <Link to="/parovani-poptavek" className="rh-a2-link" data-testid="dnesek-all-matches-link">
          {t('webAdmin.dnesek.allMatchesLink', { count: data.total })}
        </Link>
      </p>
    </>
  );
}
